import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faXmark } from '@fortawesome/free-solid-svg-icons';
import { AdminNameCell, AdminRowActions, rowClickHandler } from '../admin/AdminRowCells';
import { GROCERY_API_PATHS } from '../constants/apiPaths';
import axiosGroceryInstance from './AxiosGroceryInstance';
import {
  createInventoryItem,
  deleteInventoryItem,
  fetchGroceryProducts,
  fetchInventoryItemDetail,
  fetchInventoryItemsByOwner,
  getInventoryBarcode,
  getInventoryImageUrl,
  getInventoryPackLabel,
  getInventorySellingPrice,
  getInventoryStatus,
  updateInventoryItem,
} from './inventoryApi';
import { downloadInventoryLabelsPdf } from './inventoryLabelPdf';
import { fetchStoreProductPublicBaseUrl } from './productPublicUrl';
import './css/Inventory.css';
import './css/Menu.css';
import './css/Order.css';
import './css/Purchase.css';
import './css/UserManagement.css';
import { SEARCHABLE_SELECT_MIN_OPTIONS } from './SearchableSelect';
import defaultFoodImage from './assets/DefaultFood.jpg';
import { defaultGroceryImage } from './groceryApi';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, sortList, str } from './tableSortUtils';
import { getInventoryOwnerContext, getInventoryOwnerRequiredMessage } from './inventoryEntity';
import { createStoreCatalogItem, updateStoreCatalogItem } from './groceryApi';
import { confirmAction } from './ui/feedback';

function getInventorySortValue(row, sortKey) {
  switch (sortKey) {
    case 'inventoryItemId':
      return Number(row.inventoryItemId ?? row.Inventory_Item_Id) || 0;
    case 'itemName':
      return str(row.itemName ?? row.Item_Name);
    case 'description':
      return str(row.description ?? row.Description);
    case 'category':
      return str(row.groceryCategoryName ?? row.Grocery_Category_Name ?? row.groceryCategoryId);
    case 'brand':
      return str(row.brandName ?? row.Brand_Name ?? row.brandId ?? '');
    case 'unit':
      return str(
        getInventoryPackLabel(row) ||
          row.unitName ||
          row.Unit_Name ||
          row.unitSymbol ||
          row.unit_symbol
      );
    case 'unitPrice':
      return Number.parseFloat(getInventorySellingPrice(row)) || 0;
    case 'mrp':
      return Number.parseFloat(row.mrp ?? row.MRP) || 0;
    case 'barcode':
      return str(getInventoryBarcode(row));
    case 'status':
      return str(getInventoryStatus(row));
    case 'quantityInStock':
      return Number.parseFloat(row.quantityInStock ?? row.Quantity_In_Stock) || 0;
    case 'reorderLevel':
      return Number.parseFloat(row.reorderLevel ?? row.Reorder_Level) || 0;
    case 'reorderQuantity':
      return Number.parseFloat(row.reorderQuantity ?? row.Reorder_Quantity) || 0;
    case 'comments':
      return str(row.comments ?? row.Comments);
    default:
      return '';
  }
}

function inventoryRowMatchesSearch(row, query) {
  const q = String(query ?? '').trim().toLowerCase();
  if (!q) return true;
  const fields = [
    row.inventoryItemId,
    row.Inventory_Item_Id,
    row.itemName,
    row.Item_Name,
    row.description,
    row.Description,
    row.groceryCategoryName,
    row.Grocery_Category_Name,
    row.brandName,
    row.Brand_Name,
    row.unitName,
    row.Unit_Name,
    row.unitSymbol,
    row.unit_symbol,
    row.unitPrice,
    row.Unit_Price,
    row.mrp,
    row.MRP,
    getInventoryBarcode(row),
    getInventoryStatus(row),
    row.quantityInStock,
    row.reorderLevel,
    row.reorderQuantity,
    row.comments,
  ];
  return fields.some((v) => v != null && String(v).toLowerCase().includes(q));
}

function formatInventoryPrice(val) {
  if (val == null || val === '') return '—';
  const amount = Number.parseFloat(val);
  if (!Number.isFinite(amount)) return String(val);
  return `₹${amount.toFixed(2)}`;
}

function InventoryItemStatusPill({ status }) {
  const normalized = String(status ?? '').trim().toUpperCase();
  if (!normalized) return '—';
  const tone =
    normalized === 'ACTIVE' ? 'active' : normalized === 'INACTIVE' ? 'inactive' : 'inactive';
  return <span className={`user-status-pill user-status-pill--${tone}`}>{normalized}</span>;
}

function getInventoryStockStatus(row) {
  const qty = Number.parseFloat(row?.quantityInStock ?? row?.Quantity_In_Stock);
  const reorder = Number.parseFloat(row?.reorderLevel ?? row?.Reorder_Level);
  if (!Number.isFinite(qty) || qty <= 0) return 'out';
  if (Number.isFinite(reorder) && reorder > 0 && qty <= reorder) return 'low';
  return 'ok';
}

function toDateInputValue(value) {
  if (value == null || value === '') return '';
  const text = String(value).trim();
  if (!text) return '';
  return text.length >= 10 ? text.slice(0, 10) : text;
}

const initialForm = {
  groceryProductId: '',
  itemName: '',
  description: '',
  unitId: '',
  parentCategoryId: '',
  groceryCategoryId: '',
  brandId: '',
  unitPrice: '',
  packQuantity: '',
  mrp: '',
  barcode: '',
  status: 'ACTIVE',
  quantityInStock: '',
  reorderLevel: '',
  reorderQuantity: '',
  comments: '',
  mfgDate: '',
  expDate: '',
  imageFile: null,
  existingImageUrl: '',
};

const getUnitId = (unit) => unit?.unitId ?? unit?.Unit_Id ?? unit?.unit_id ?? '';
const getUnitName = (unit) => unit?.unitName ?? unit?.Unit_Name ?? unit?.unit_name ?? '';
const getCategoryId = (category) =>
  category?.groceryCategoryId ?? category?.Grocery_Category_Id ?? category?.grocery_category_id ?? '';
const getCategoryName = (category) =>
  category?.categoryName ?? category?.Category_Name ?? category?.category_name ?? '';
const getParentCategoryId = (category) =>
  category?.parentCategoryId ?? category?.Parent_Category_Id ?? category?.parent_category_id ?? '';

function resolveParentCategoryId(categoriesList, groceryCategoryId, explicitParentId) {
  if (explicitParentId != null && explicitParentId !== '') {
    return String(explicitParentId);
  }
  const leaf = (categoriesList || []).find(
    (c) => String(getCategoryId(c)) === String(groceryCategoryId)
  );
  const parentId = getParentCategoryId(leaf);
  return parentId != null && parentId !== '' ? String(parentId) : '';
}

function buildFormStateFromInventoryRow(row, categoriesList = []) {
  if (!row || typeof row !== 'object') return { ...initialForm };
  const groceryCategoryId = String(row.groceryCategoryId ?? row.Grocery_Category_Id ?? '');
  const parentCategoryId = resolveParentCategoryId(
    categoriesList,
    groceryCategoryId,
    row.parentCategoryId ?? row.Parent_Category_Id
  );
  return {
    groceryProductId: String(row.groceryId ?? row.Grocery_Id ?? row.groceryProductId ?? ''),
    itemName: String(row.itemName ?? row.Item_Name ?? ''),
    description: String(row.description ?? row.Description ?? ''),
    unitId: String(row.unitId ?? row.Unit_Id ?? ''),
    parentCategoryId,
    groceryCategoryId,
    brandId: String(row.brandId ?? row.Brand_Id ?? ''),
    unitPrice: String(
      row.sellingPrice ?? row.Selling_Price ?? row.unitPrice ?? row.Unit_Price ?? ''
    ),
    packQuantity:
      row.packQuantity != null && row.packQuantity !== ''
        ? String(row.packQuantity ?? row.Pack_Quantity)
        : '',
    mrp: row.mrp != null && row.mrp !== '' ? String(row.mrp ?? row.MRP) : '',
    barcode: String(row.barcode ?? row.Barcode ?? ''),
    status: String(row.status ?? row.Status ?? 'ACTIVE'),
    quantityInStock:
      row.quantityInStock !== undefined && row.quantityInStock !== null
        ? String(row.quantityInStock)
        : '',
    reorderLevel:
      row.reorderLevel !== undefined && row.reorderLevel !== null ? String(row.reorderLevel) : '',
    reorderQuantity:
      row.reorderQuantity !== undefined && row.reorderQuantity !== null
        ? String(row.reorderQuantity)
        : '',
    comments: row.comments != null ? String(row.comments) : '',
    mfgDate: toDateInputValue(row.mfgDate ?? row.Mfg_Date),
    expDate: toDateInputValue(row.expDate ?? row.Exp_Date),
    imageFile: null,
    existingImageUrl: getInventoryImageUrl(row) || '',
  };
}

const Inventory = () => {
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [groceryProducts, setGroceryProducts] = useState([]);
  const [groceriesLoading, setGroceriesLoading] = useState(false);
  const [unitSearch, setUnitSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showUnitDropdown, setShowUnitDropdown] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const unitDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const productDropdownRef = useRef(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [editingItemId, setEditingItemId] = useState(null);
  const [inventoryDetailLoading, setInventoryDetailLoading] = useState(false);
  const [barcodeEditable, setBarcodeEditable] = useState(false);
  const [savedBarcode, setSavedBarcode] = useState('');
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryPageSize, setInventoryPageSize] = useState(10);
  const [inventorySort, setInventorySort] = useState(null);
  const [listSearch, setListSearch] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [selectedLabelIds, setSelectedLabelIds] = useState(() => new Set());
  const [labelsDownloading, setLabelsDownloading] = useState(false);

  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch (e) {
      return null;
    }
  }, []);

  const owner = getInventoryOwnerContext();
  const { ownerId, isStore, restaurantId, storeId } = owner;
  const inventoryDefaultImage = isStore ? defaultGroceryImage : defaultFoodImage;
  const createdById = Number(user?.userId || 0);
  const inventoryColCount = isStore ? 15 : 10;

  const resetForm = () => {
    setForm({ ...initialForm });
    setUnitSearch('');
    setCategorySearch('');
    setShowUnitDropdown(false);
    setShowCategoryDropdown(false);
    setEditingItemId(null);
    setInventoryDetailLoading(false);
    setBarcodeEditable(false);
    setSavedBarcode('');
  };

  const handleOpen = () => {
    setStatusMessage('');
    resetForm();
    setShowModal(true);
  };

  const handleEdit = async (row) => {
    const id = row.inventoryItemId ?? row.Inventory_Item_Id ?? null;
    if (!id) return;

    const initial = buildFormStateFromInventoryRow(row, categories);
    setForm(initial);
    setSavedBarcode(initial.barcode || '');
    setBarcodeEditable(false);
    setEditingItemId(id);
    setUnitSearch('');
    setCategorySearch('');
    setShowUnitDropdown(false);
    setShowCategoryDropdown(false);
    setStatusMessage('');
    setShowModal(true);
    setInventoryDetailLoading(true);

    try {
      const detail = await fetchInventoryItemDetail(id, owner);
      if (detail) {
        const next = buildFormStateFromInventoryRow(detail, categories);
        setForm(next);
        setSavedBarcode(next.barcode || '');
      }
    } catch (error) {
      console.error(
        'Failed to load inventory item:',
        error?.response?.status,
        error?.response?.data ?? error?.message
      );
      setStatusMessage('Could not load latest details from server; values shown are from the list.');
    } finally {
      setInventoryDetailLoading(false);
    }
  };

  const handleClose = () => {
    setShowModal(false);
    resetForm();
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setForm((prev) => {
      const next = {
        ...prev,
        [name]: type === 'file' ? files?.[0] ?? null : value,
      };
      if (name === 'parentCategoryId') {
        next.groceryCategoryId = '';
        next.groceryProductId = '';
      }
      if (name === 'groceryCategoryId') {
        // Keep typed name; drop master link if category no longer matches later via suggestions.
        next.groceryProductId = '';
      }
      return next;
    });
  };

  const handleDelete = async (itemId) => {
    if (!itemId) return;
    const ok = await confirmAction({
      title: 'Delete inventory item?',
      message: 'Delete this inventory item?',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;

    try {
      const data = await deleteInventoryItem(itemId, owner);
      await fetchInventoryItems();
      alert(data?.message || 'Inventory item deleted');
    } catch (error) {
      console.log(error?.response?.data?.message || 'Failed to delete inventory item.');
      alert(error?.response?.data?.message || 'Failed to delete inventory item.');
    }
  };

  const fetchInventoryItems = async () => {
    if (!ownerId) {
      setInventoryItems([]);
      return;
    }
    try {
      setItemsLoading(true);
      const rows = await fetchInventoryItemsByOwner(owner);
      setInventoryItems(rows);
    } catch (error) {
      console.error(
        'Failed to load inventory:',
        error?.response?.status,
        error?.response?.data ?? error?.message
      );
      setInventoryItems([]);
    } finally {
      setItemsLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryItems();
  }, [ownerId, isStore]);

  useEffect(() => {
    setInventoryPage(1);
    setListSearch('');
    setStockFilter('all');
    setSelectedLabelIds(new Set());
  }, [ownerId, isStore]);

  const listSearchQuery = listSearch.trim();

  const toggleStockFilter = (next) => {
    setStockFilter((prev) => (prev === next ? 'all' : next));
    setInventoryPage(1);
  };

  const filteredInventoryItems = useMemo(
    () =>
      inventoryItems.filter((row) => {
        if (!inventoryRowMatchesSearch(row, listSearchQuery)) return false;
        if (stockFilter === 'all') return true;
        return getInventoryStockStatus(row) === stockFilter;
      }),
    [inventoryItems, listSearchQuery, stockFilter]
  );

  useEffect(() => {
    setInventoryPage(1);
  }, [listSearchQuery, stockFilter]);

  const sortedInventoryItems = useMemo(
    () => sortList(filteredInventoryItems, inventorySort, getInventorySortValue),
    [filteredInventoryItems, inventorySort]
  );

  const selectableLabelRows = useMemo(() => {
    if (!isStore) return [];
    return sortedInventoryItems.filter((row) => {
      const id = row.inventoryItemId ?? row.Inventory_Item_Id;
      const name = String(row.itemName ?? row.Item_Name ?? '').trim();
      return id != null && Boolean(name);
    });
  }, [isStore, sortedInventoryItems]);

  const allFilteredLabelsSelected =
    selectableLabelRows.length > 0 &&
    selectableLabelRows.every((row) =>
      selectedLabelIds.has(String(row.inventoryItemId ?? row.Inventory_Item_Id))
    );

  const toggleLabelSelection = (rowId, checked) => {
    const key = String(rowId);
    setSelectedLabelIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const toggleSelectAllLabels = (checked) => {
    setSelectedLabelIds((prev) => {
      const next = new Set(prev);
      selectableLabelRows.forEach((row) => {
        const key = String(row.inventoryItemId ?? row.Inventory_Item_Id);
        if (checked) next.add(key);
        else next.delete(key);
      });
      return next;
    });
  };

  const handleDownloadLabelsPdf = async () => {
    if (!isStore || labelsDownloading) return;
    const selectedRows = sortedInventoryItems.filter((row) => {
      const id = row.inventoryItemId ?? row.Inventory_Item_Id;
      return id != null && selectedLabelIds.has(String(id));
    });
    if (selectedRows.length === 0) {
      alert('Select at least one inventory item to download labels.');
      return;
    }
    setLabelsDownloading(true);
    try {
      const productPublicBaseUrl = await fetchStoreProductPublicBaseUrl(storeId);
      if (!productPublicBaseUrl) {
        alert(
          'Add Website under this store’s business profile → Media (social links), e.g. https://crltgroup.com, before printing product QR labels.'
        );
        return;
      }
      const result = await downloadInventoryLabelsPdf(selectedRows, {
        productPublicBaseUrl,
      });
      if (result.skipped > 0) {
        alert(
          `Downloaded ${result.count} label(s). Skipped ${result.skipped} item(s) that could not be printed.`
        );
      }
    } catch (error) {
      alert(error?.message || 'Failed to create label PDF.');
    } finally {
      setLabelsDownloading(false);
    }
  };

  const inventoryStats = useMemo(() => {
    let inStock = 0;
    let lowStock = 0;
    let outOfStock = 0;
    inventoryItems.forEach((row) => {
      const status = getInventoryStockStatus(row);
      if (status === 'ok') inStock += 1;
      else if (status === 'low') lowStock += 1;
      else outOfStock += 1;
    });
    return {
      total: inventoryItems.length,
      inStock,
      lowStock,
      outOfStock,
    };
  }, [inventoryItems]);

  const { handleSortAsc: handleInventorySortAsc, handleSortDesc: handleInventorySortDesc } =
    buildSortHandlers(setInventorySort, () => setInventoryPage(1));

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(sortedInventoryItems.length / inventoryPageSize));
    setInventoryPage((p) => Math.min(p, tp));
  }, [sortedInventoryItems.length, inventoryPageSize]);

  const inventoryTotalPages = Math.max(
    1,
    Math.ceil(sortedInventoryItems.length / inventoryPageSize)
  );
  const inventoryPageSafe = Math.min(Math.max(1, inventoryPage), inventoryTotalPages);
  const inventoryPageStart = (inventoryPageSafe - 1) * inventoryPageSize;
  const pagedInventoryItems = sortedInventoryItems.slice(
    inventoryPageStart,
    inventoryPageStart + inventoryPageSize
  );

  const handleInventoryPageChange = (nextPage) => {
    setInventoryPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleInventoryPageSizeChange = (nextSize) => {
    setInventoryPageSize(nextSize);
    setInventoryPage(1);
  };

  useEffect(() => {
    const fetchUnits = async () => {
      try {
        setUnitsLoading(true);
        const token = localStorage.getItem('Token');
        const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.units, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.units)
            ? response.data.units
            : [];
        setUnits(list);
      } catch (error) {
        setUnits([]);
      } finally {
        setUnitsLoading(false);
      }
    };

    fetchUnits();
  }, []);

  const unitsFromGroceries = useMemo(() => {
    const map = new Map();
    groceryProducts.forEach((p) => {
      const uid = p.unitId;
      if (uid == null || uid === '' || map.has(String(uid))) return;
      map.set(String(uid), {
        unitId: uid,
        Unit_Id: uid,
        unitName: p.unitName,
        Unit_Name: p.unitName,
        unitSymbol: p.unitSymbol,
      });
    });
    return [...map.values()];
  }, [groceryProducts]);

  const categoriesFromGroceries = useMemo(() => {
    const map = new Map();
    groceryProducts.forEach((p) => {
      const cid = p.groceryCategoryId;
      if (cid == null || cid === '' || map.has(String(cid))) return;
      map.set(String(cid), {
        groceryCategoryId: cid,
        Grocery_Category_Id: cid,
        categoryName: p.groceryCategoryName,
        Category_Name: p.groceryCategoryName,
        parentCategoryId: p.parentCategoryId ?? null,
      });
    });
    return [...map.values()];
  }, [groceryProducts]);

  const categoriesForForm = categories.length > 0 ? categories : categoriesFromGroceries;

  const rootCategories = useMemo(
    () =>
      categoriesForForm.filter((category) => {
        const parentId = getParentCategoryId(category);
        return parentId == null || parentId === '';
      }),
    [categoriesForForm]
  );

  const hasCategoryHierarchy = rootCategories.some((root) =>
    categoriesForForm.some(
      (c) => String(getParentCategoryId(c)) === String(getCategoryId(root))
    )
  );

  const subcategoryOptions = useMemo(() => {
    if (!form.parentCategoryId) {
      return hasCategoryHierarchy ? [] : categoriesForForm;
    }
    return categoriesForForm.filter(
      (c) => String(getParentCategoryId(c)) === String(form.parentCategoryId)
    );
  }, [categoriesForForm, form.parentCategoryId, hasCategoryHierarchy]);

  const selectedParentHasChildren = subcategoryOptions.length > 0;

  const leafCategories = useMemo(() => {
    if (!hasCategoryHierarchy) return categoriesForForm;
    const parentIds = new Set(
      categoriesForForm
        .map((c) => getParentCategoryId(c))
        .filter((id) => id != null && id !== '')
        .map(String)
    );
    return categoriesForForm.filter((c) => !parentIds.has(String(getCategoryId(c))));
  }, [categoriesForForm, hasCategoryHierarchy]);

  // Standalone roots (e.g. Beverages) are both category and leaf — no subcategory step.
  useEffect(() => {
    if (!hasCategoryHierarchy || !form.parentCategoryId) return;
    if (selectedParentHasChildren) return;
    if (String(form.groceryCategoryId) === String(form.parentCategoryId)) return;
    setForm((prev) => ({
      ...prev,
      groceryCategoryId: String(prev.parentCategoryId),
    }));
  }, [
    form.parentCategoryId,
    form.groceryCategoryId,
    hasCategoryHierarchy,
    selectedParentHasChildren,
  ]);

  const unitsForForm = isStore
    ? units
    : groceryProducts.length > 0
      ? unitsFromGroceries
      : units;

  const filteredUnits = unitsForForm.filter((unit) =>
    String(getUnitName(unit)).toLowerCase().includes(unitSearch.toLowerCase())
  );

  const filteredCategories = (isStore ? subcategoryOptions : leafCategories).filter((category) =>
    String(getCategoryName(category)).toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredGroceryProducts = groceryProducts.filter((p) => {
    const q = (isStore ? form.itemName : productSearch).toLowerCase().trim();
    if (!q) return !isStore;
    return (
      String(p.productName || '').toLowerCase().includes(q) ||
      String(p.productCode || '').toLowerCase().includes(q) ||
      String(p.groceryCategoryName || '').toLowerCase().includes(q) ||
      String(p.unitName || '').toLowerCase().includes(q)
    );
  });

  const categoryFilterIds = useMemo(() => {
    if (!isStore) return null;
    if (form.groceryCategoryId) {
      return new Set([String(form.groceryCategoryId)]);
    }
    if (form.parentCategoryId) {
      const ids = new Set([String(form.parentCategoryId)]);
      categoriesForForm.forEach((c) => {
        if (String(getParentCategoryId(c)) === String(form.parentCategoryId)) {
          ids.add(String(getCategoryId(c)));
        }
      });
      return ids;
    }
    return null;
  }, [
    isStore,
    form.groceryCategoryId,
    form.parentCategoryId,
    categoriesForForm,
  ]);

  const itemNameSuggestions = useMemo(() => {
    if (!isStore) return [];
    let list = groceryProducts;
    if (categoryFilterIds) {
      list = list.filter((p) => {
        const cid = String(p.groceryCategoryId ?? '');
        const pid = String(p.parentCategoryId ?? '');
        return categoryFilterIds.has(cid) || (pid && categoryFilterIds.has(pid));
      });
    }
    const q = String(form.itemName || '').trim().toLowerCase();
    if (q) {
      list = list.filter((p) => {
        const name = String(p.productName || '').toLowerCase();
        const code = String(p.productCode || '').toLowerCase();
        return name.includes(q) || code.includes(q);
      });
    } else if (!categoryFilterIds) {
      return [];
    }
    return list.slice(0, 20);
  }, [isStore, form.itemName, groceryProducts, categoryFilterIds]);

  const storeCategorySelected = Boolean(
    form.groceryCategoryId || form.parentCategoryId
  );
  const selectedUnit = unitsForForm.find((unit) => String(getUnitId(unit)) === String(form.unitId));
  const selectedCategory = categoriesForForm.find(
    (category) => String(getCategoryId(category)) === String(form.groceryCategoryId)
  );
  const selectedGroceryProduct = groceryProducts.find(
    (p) => String(p.id) === String(form.groceryProductId)
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (unitDropdownRef.current && !unitDropdownRef.current.contains(event.target)) {
        setShowUnitDropdown(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
      if (productDropdownRef.current && !productDropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setCategoriesLoading(true);
        const token = localStorage.getItem('Token');
        const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.categories, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.categories)
            ? response.data.categories
            : [];
        setCategories(list);
      } catch (error) {
        setCategories([]);
      } finally {
        setCategoriesLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    if (!isStore) {
      setBrands([]);
      return undefined;
    }
    const fetchBrands = async () => {
      try {
        setBrandsLoading(true);
        const token = localStorage.getItem('Token');
        const response = await axiosGroceryInstance.get(GROCERY_API_PATHS.brands, {
          headers: {
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        });
        const list = Array.isArray(response.data?.brands)
          ? response.data.brands
          : Array.isArray(response.data)
            ? response.data
            : [];
        setBrands(list);
      } catch (error) {
        setBrands([]);
      } finally {
        setBrandsLoading(false);
      }
    };

    fetchBrands();
    return undefined;
  }, [isStore]);

  useEffect(() => {
    const loadGroceries = async () => {
      try {
        setGroceriesLoading(true);
        const list = await fetchGroceryProducts();
        setGroceryProducts(list);
      } catch (error) {
        setGroceryProducts([]);
      } finally {
        setGroceriesLoading(false);
      }
    };
    loadGroceries();
  }, []);

  const selectGroceryProduct = (product) => {
    const leafId = String(product.groceryCategoryId ?? '');
    const parentId = resolveParentCategoryId(
      categories,
      leafId,
      product.parentCategoryId
    );
    setForm((prev) => ({
      ...prev,
      groceryProductId: String(product.id),
      itemName: product.productName || prev.itemName,
      unitId: String(product.unitId ?? prev.unitId ?? ''),
      parentCategoryId: parentId || prev.parentCategoryId,
      groceryCategoryId: leafId || prev.groceryCategoryId,
    }));
    setShowProductDropdown(false);
    setProductSearch('');
  };

  const handleItemNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const selected = groceryProducts.find(
        (p) => String(p.id) === String(prev.groceryProductId)
      );
      const next = { ...prev, itemName: value };
      if (
        selected &&
        String(selected.productName || '').trim().toLowerCase() !==
          String(value || '').trim().toLowerCase()
      ) {
        next.groceryProductId = '';
      }
      return next;
    });
    if (isStore) {
      setShowProductDropdown(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatusMessage('');

    if (!ownerId) {
      setStatusMessage(getInventoryOwnerRequiredMessage());
      return;
    }
    if (!createdById) {
      setStatusMessage('User not found. Please sign in again.');
      return;
    }

    const qty = Number(form.quantityInStock);
    const reorderAt = Number(form.reorderLevel);
    const reorderQty = Number(form.reorderQuantity);
    if (
      form.quantityInStock === '' ||
      Number.isNaN(qty) ||
      form.reorderLevel === '' ||
      Number.isNaN(reorderAt) ||
      form.reorderQuantity === '' ||
      Number.isNaN(reorderQty)
    ) {
      setStatusMessage('Please enter valid numbers for stock, reorder level, and reorder quantity.');
      return;
    }

    const levelPayload = {
      quantityInStock: qty,
      reorderLevel: reorderAt,
      reorderQuantity: reorderQty,
      comments: String(form.comments ?? '').trim(),
      mfgDate: form.mfgDate || undefined,
      expDate: form.expDate || undefined,
      levelCreatedById: createdById,
    };

    if (isStore) {
      if (!form.itemName.trim()) {
        setStatusMessage('Item name is required.');
        return;
      }
      if (form.unitPrice === '' || Number.isNaN(Number(form.unitPrice))) {
        setStatusMessage('Selling price is required.');
        return;
      }
      if (!editingItemId && !form.groceryCategoryId) {
        setStatusMessage('Category is required.');
        return;
      }
      if (!form.unitId) {
        setStatusMessage('Unit is required (e.g. kg, g, L).');
        return;
      }
      const packQty = Number(form.packQuantity);
      if (form.packQuantity === '' || Number.isNaN(packQty) || packQty <= 0) {
        setStatusMessage('Pack quantity is required (e.g. 1 for a 1 kg pack).');
        return;
      }

      const catalogPayload = {
        storeId: Number(storeId),
        itemName: form.itemName.trim(),
        sellingPrice: Number(form.unitPrice),
        packQuantity: packQty,
        unitId: Number(form.unitId),
        status: form.status || 'ACTIVE',
        description: form.description.trim() || undefined,
      };
      if (editingItemId && barcodeEditable) {
        catalogPayload.barcode = form.barcode.trim() || undefined;
      }
      if (form.mrp !== '' && !Number.isNaN(Number(form.mrp))) {
        catalogPayload.mrp = Number(form.mrp);
      }
      if (form.groceryCategoryId) {
        catalogPayload.groceryCategoryId = Number(form.groceryCategoryId);
      }
      if (form.brandId) {
        catalogPayload.brandId = Number(form.brandId);
      } else if (editingItemId) {
        catalogPayload.brandId = null;
      }
      if (form.groceryProductId) {
        catalogPayload.groceryId = Number(form.groceryProductId);
      }
      if (form.mfgDate) {
        catalogPayload.mfgDate = form.mfgDate;
      }
      if (form.expDate) {
        catalogPayload.expDate = form.expDate;
      }

      try {
        setIsSubmitting(true);
        let itemId = editingItemId;
        if (editingItemId) {
          await updateStoreCatalogItem(
            editingItemId,
            { ...catalogPayload, updatedBy: createdById },
            form.imageFile
          );
          await updateInventoryItem(
            editingItemId,
            {
              storeId,
              itemName: form.itemName.trim(),
              description: form.description.trim(),
              unitPrice: String(form.unitPrice).trim(),
              updatedById: createdById,
              level: levelPayload,
            },
            owner
          );
        } else {
          const created = await createStoreCatalogItem(
            { ...catalogPayload, createdBy: createdById },
            form.imageFile
          );
          itemId = created?.groceryItemId;
          if (itemId) {
            await updateInventoryItem(
              itemId,
              {
                storeId,
                updatedById: createdById,
                level: levelPayload,
              },
              owner
            );
          }
        }
        await fetchInventoryItems();
        handleClose();
        alert(editingItemId ? 'Inventory item updated' : 'Inventory item created');
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          (editingItemId ? 'Failed to update inventory item.' : 'Failed to create inventory item.');
        setStatusMessage(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    const basePayload = {
          restaurantId,
          itemName: form.itemName.trim(),
          description: form.description.trim(),
          unitId: Number(form.unitId),
          groceryCategoryId: Number(form.groceryCategoryId),
          unitPrice: String(form.unitPrice).trim(),
          mfgDate: form.mfgDate || undefined,
          expDate: form.expDate || undefined,
        };

    if (!editingItemId) {
      if (!form.groceryProductId) {
        setStatusMessage('Please select a grocery product.');
        return;
      }
      basePayload.groceryId = Number(form.groceryProductId);
    }

    if (
      !basePayload.itemName ||
      !basePayload.unitPrice ||
      !basePayload.unitId ||
      !basePayload.groceryCategoryId ||
      (!editingItemId && (!basePayload.groceryId || Number.isNaN(basePayload.groceryId)))
    ) {
      setStatusMessage('Please fill all required fields.');
      return;
    }

    try {
      setIsSubmitting(true);
      const response = editingItemId
        ? await updateInventoryItem(
            editingItemId,
            {
              ...basePayload,
              updatedById: createdById,
              level: levelPayload,
              imageFile: form.imageFile,
            },
            owner
          )
        : await createInventoryItem({
            ...basePayload,
            createdById,
            level: levelPayload,
            imageFile: form.imageFile,
          });

      // setStatusMessage(
      //   response?.data?.message || (editingItemId ? 'Inventory item updated' : 'Inventory item created')
      // );
      await fetchInventoryItems();
      handleClose();
      const successMsg =
        response?.message ?? response?.data?.message ?? (editingItemId ? 'Inventory item updated' : 'Inventory item created');
      alert(successMsg);
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        (editingItemId ? 'Failed to update inventory item.' : 'Failed to create inventory item.');
      setStatusMessage(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="menu inventory-page purchase-page">
      <div className="inventory-supplier-panel">
        {statusMessage ? <p className="inventory-status">{statusMessage}</p> : null}

        {!ownerId ? (
          <p className="inventory-status">{getInventoryOwnerRequiredMessage()}</p>
        ) : null}

        {ownerId > 0 ? (
          <>
            <div className="purchase-stats-grid purchase-stats-grid--inventory">
              <button
                type="button"
                className={`purchase-stat-card${stockFilter === 'all' ? ' purchase-stat-card--active' : ''}`}
                onClick={() => toggleStockFilter('all')}
                aria-pressed={stockFilter === 'all'}
              >
                <span className="purchase-stat-label">Total items</span>
                <span className="purchase-stat-value purchase-stat-value--primary">
                  {inventoryStats.total}
                </span>
              </button>
              <button
                type="button"
                className={`purchase-stat-card${stockFilter === 'ok' ? ' purchase-stat-card--active' : ''}`}
                onClick={() => toggleStockFilter('ok')}
                aria-pressed={stockFilter === 'ok'}
              >
                <span className="purchase-stat-label">In stock</span>
                <span className="purchase-stat-value purchase-stat-value--success">
                  {inventoryStats.inStock}
                </span>
              </button>
              <button
                type="button"
                className={`purchase-stat-card${stockFilter === 'low' ? ' purchase-stat-card--active' : ''}`}
                onClick={() => toggleStockFilter('low')}
                aria-pressed={stockFilter === 'low'}
              >
                <span className="purchase-stat-label">Low stock</span>
                <span className="purchase-stat-value purchase-stat-value--warning">
                  {inventoryStats.lowStock}
                </span>
              </button>
              <button
                type="button"
                className={`purchase-stat-card${stockFilter === 'out' ? ' purchase-stat-card--active' : ''}`}
                onClick={() => toggleStockFilter('out')}
                aria-pressed={stockFilter === 'out'}
              >
                <span className="purchase-stat-label">Out of stock</span>
                <span className="purchase-stat-value">{inventoryStats.outOfStock}</span>
              </button>
            </div>

            <div className="purchase-content-card">
              <div className="inventory-list-toolbar menu-toolbar">
                <div className="menu-toolbar-row">
                  <div className="menu-food-filter-wrap">
                    <input
                      type="text"
                      inputMode="search"
                      autoComplete="off"
                      className="menu-food-filter-input"
                      placeholder="Search inventory…"
                      value={listSearch}
                      onChange={(e) => setListSearch(e.target.value)}
                      aria-label="Search inventory items"
                      role="searchbox"
                    />
                    {listSearch.trim() !== '' && (
                      <button
                        type="button"
                        className="menu-food-filter-clear"
                        aria-label="Clear search"
                        onClick={() => setListSearch('')}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    )}
                  </div>
                  <div className="menu-toolbar-spacer" />
                  {isStore ? (
                    <div className="purchase-toolbar-actions">
                      <button
                        type="button"
                        className="purchase-btn purchase-btn--secondary"
                        onClick={handleDownloadLabelsPdf}
                        disabled={labelsDownloading || selectedLabelIds.size === 0}
                        title={
                          selectedLabelIds.size === 0
                            ? 'Select products to download label PDF (name + QR → product page)'
                            : 'Download product labels PDF (name, border, QR to product page)'
                        }
                      >
                        <FontAwesomeIcon icon={faDownload} />{' '}
                        {labelsDownloading
                          ? 'Preparing PDF…'
                          : selectedLabelIds.size > 0
                            ? `Download labels (${selectedLabelIds.size})`
                            : 'Download labels'}
                      </button>
                      <button type="button" className="purchase-btn purchase-btn--create" onClick={handleOpen}>
                        Add Inventory
                      </button>
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="menu-table-wrapper">
          <table className="menu-table inventory-items-table admin-rows-table">
            <thead>
              <tr>
                <OrdersSortableTh className="inventory-item-name-th" label="Name" sortKey="itemName" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                <OrdersSortableTh label="Description" sortKey="description" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                <OrdersSortableTh label="Category" sortKey="category" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                {isStore ? (
                  <OrdersSortableTh label="Brand" sortKey="brand" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                ) : null}
                <OrdersSortableTh label={isStore ? 'Pack size' : 'Unit'} sortKey="unit" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                {isStore ? (
                  <OrdersSortableTh label="Barcode" sortKey="barcode" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                ) : null}
                {isStore ? (
                  <OrdersSortableTh label="MRP" sortKey="mrp" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                ) : null}
                <OrdersSortableTh
                  label={isStore ? 'Selling price' : 'Unit price'}
                  sortKey="unitPrice"
                  activeSortKey={inventorySort?.key}
                  sortDirection={inventorySort?.direction}
                  onSortAsc={handleInventorySortAsc}
                  onSortDesc={handleInventorySortDesc}
                />
                {isStore ? (
                  <OrdersSortableTh label="Status" sortKey="status" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                ) : null}
                <OrdersSortableTh label="In stock" sortKey="quantityInStock" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                <OrdersSortableTh label="Reorder at" sortKey="reorderLevel" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                <OrdersSortableTh label="Reorder qty" sortKey="reorderQuantity" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                <OrdersSortableTh label="Comments" sortKey="comments" activeSortKey={inventorySort?.key} sortDirection={inventorySort?.direction} onSortAsc={handleInventorySortAsc} onSortDesc={handleInventorySortDesc} />
                {isStore ? (
                  <th className="inventory-label-check-th" scope="col">
                    <input
                      type="checkbox"
                      className="inventory-label-checkbox"
                      checked={allFilteredLabelsSelected}
                      disabled={selectableLabelRows.length === 0}
                      onChange={(e) => toggleSelectAllLabels(e.target.checked)}
                      aria-label="Select all products for label PDF"
                      title={
                        selectableLabelRows.length === 0
                          ? 'No products in the current list'
                          : 'Select all filtered products for label PDF'
                      }
                    />
                  </th>
                ) : null}
                <th className="menu-table-actions-head">Actions</th>
              </tr>
            </thead>
            <tbody>
              {itemsLoading ? (
                <tr>
                  <td colSpan={inventoryColCount} className="inventory-items-hint inventory-table-empty-cell">
                    Loading inventory...
                  </td>
                </tr>
              ) : inventoryItems.length === 0 ? (
                <tr>
                  <td colSpan={inventoryColCount} className="inventory-items-hint inventory-table-empty-cell">
                    No inventory items yet.
                  </td>
                </tr>
              ) : filteredInventoryItems.length === 0 ? (
                <tr>
                  <td colSpan={inventoryColCount} className="inventory-items-hint inventory-table-empty-cell">
                    {stockFilter !== 'all' || listSearchQuery
                      ? 'No inventory items match your filters.'
                      : 'No inventory items yet.'}
                  </td>
                </tr>
              ) : (
                pagedInventoryItems.map((row) => {
                  const invId = row.inventoryItemId ?? row.Inventory_Item_Id;
                  const name = row.itemName ?? row.Item_Name ?? '—';
                  const barcode = getInventoryBarcode(row);
                  const rowKey = invId != null ? String(invId) : '';
                  const isLabelSelected = rowKey !== '' && selectedLabelIds.has(rowKey);
                  const detailPath =
                    invId != null ? `/inventory/item/${encodeURIComponent(invId)}` : null;
                  const openDetail = detailPath ? () => navigate(detailPath) : undefined;
                  const itemStatus = getInventoryStatus(row);
                  return (
                  <tr
                    key={invId ?? `row-${row.itemName}`}
                    className={openDetail ? 'eats-admin-row--clickable' : undefined}
                    onClick={rowClickHandler(openDetail)}
                  >
                    <td className="inventory-item-name-td">
                      <div className="inventory-item-name-cell">
                        <img
                          src={getInventoryImageUrl(row) || inventoryDefaultImage}
                          alt={name}
                          className={`inventory-item-thumb${
                            isStore && !getInventoryImageUrl(row)
                              ? ' inventory-grocery-placeholder'
                              : ''
                          }`}
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = inventoryDefaultImage;
                            if (isStore) {
                              e.target.classList.add('inventory-grocery-placeholder');
                            }
                          }}
                        />
                        <AdminNameCell
                          name={name}
                          id={invId}
                          status={itemStatus}
                          to={detailPath}
                        />
                      </div>
                    </td>
                    <td>{row.description ?? row.Description ?? '—'}</td>
                    <td>{row.groceryCategoryName ?? row.Grocery_Category_Name ?? '—'}</td>
                    {isStore ? (
                      <td>{row.brandName ?? row.Brand_Name ?? '—'}</td>
                    ) : null}
                    <td>
                      {isStore
                        ? getInventoryPackLabel(row) || '—'
                        : row.unitName ?? row.Unit_Name ?? '—'}
                      {!isStore && (row.unitSymbol || row.unit_symbol) ? (
                        <span className="inventory-unit-symbol">
                          {' '}
                          ({row.unitSymbol ?? row.unit_symbol})
                        </span>
                      ) : null}
                    </td>
                    {isStore ? (
                      <td>{barcode || '—'}</td>
                    ) : null}
                    {isStore ? (
                      <td className="purchase-amount-cell">
                        {formatInventoryPrice(row.mrp ?? row.MRP)}
                      </td>
                    ) : null}
                    <td className="purchase-amount-cell">
                      {formatInventoryPrice(getInventorySellingPrice(row))}
                    </td>
                    {isStore ? (
                      <td>
                        <InventoryItemStatusPill status={itemStatus} />
                      </td>
                    ) : null}
                    <td className="inventory-stock-cell">
                      {row.quantityInStock !== '' && row.quantityInStock != null
                        ? row.quantityInStock
                        : '—'}
                    </td>
                    <td>{row.reorderLevel !== '' && row.reorderLevel != null ? row.reorderLevel : '—'}</td>
                    <td>{row.reorderQuantity !== '' && row.reorderQuantity != null ? row.reorderQuantity : '—'}</td>
                    <td
                      className="inventory-comments-cell"
                      title={
                        row.comments != null && String(row.comments).trim() !== ''
                          ? String(row.comments)
                          : undefined
                      }
                    >
                      {row.comments != null && String(row.comments).trim() !== ''
                        ? String(row.comments)
                        : '—'}
                    </td>
                    {isStore ? (
                      <td className="inventory-label-check-td">
                        <label className="inventory-label-check-wrap">
                          <input
                            type="checkbox"
                            className="inventory-label-checkbox"
                            checked={isLabelSelected}
                            disabled={invId == null || !name || name === '—'}
                            onChange={(e) => toggleLabelSelection(invId, e.target.checked)}
                            aria-label={`Select ${name} for label PDF`}
                            title={
                              invId != null && name && name !== '—'
                                ? 'Include this product in the label PDF'
                                : 'Product id and name are required for labels'
                            }
                          />
                          <span className="inventory-label-check-text">Label</span>
                        </label>
                      </td>
                    ) : null}
                    <td className="menu-table-actions-cell">
                      <AdminRowActions
                        name={name}
                        onView={openDetail}
                        onEdit={() => handleEdit(row)}
                        onDeactivate={() => handleDelete(invId)}
                      />
                    </td>
                  </tr>
                  );
                })
              )}
            </tbody>
          </table>
              </div>

              {!itemsLoading && filteredInventoryItems.length > 0 ? (
                <OrdersTablePagination
                  currentPage={inventoryPageSafe}
                  totalPages={inventoryTotalPages}
                  totalItems={sortedInventoryItems.length}
                  pageSize={inventoryPageSize}
                  onPageChange={handleInventoryPageChange}
                  onPageSizeChange={handleInventoryPageSizeChange}
                  itemLabel="items"
                  ariaLabel="Inventory table pagination"
                />
              ) : null}
            </div>
          </>
        ) : null}
      </div>

      {showModal && (
        <div className="inventory-modal-overlay" role="presentation">
          <div
            className="inventory-modal inventory-item-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="inventory-item-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-modal-header inventory-item-modal-header">
              <h4 id="inventory-item-modal-title">
                {editingItemId ? 'Update Inventory Item' : 'Add Inventory Item'}
              </h4>
              <button
                type="button"
                className="menu-template-modal-close"
                aria-label="Close"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form className="inventory-item-form" onSubmit={handleSubmit}>
              <div className="inventory-item-form-body">
                {statusMessage ? (
                  <p className="inventory-item-form-message inventory-form-span-full">{statusMessage}</p>
                ) : null}
                {inventoryDetailLoading && editingItemId ? (
                  <p className="inventory-detail-loading-hint inventory-form-span-full">
                    Loading latest details…
                  </p>
                ) : null}

              {!editingItemId && !isStore ? (
                <div className="inventory-form-group inventory-form-span-full">
                  <label htmlFor="inventory-grocery-product">Grocery product</label>
                  <div className="inventory-searchable-select" ref={productDropdownRef}>
                    <button
                      type="button"
                      id="inventory-grocery-product"
                      className={`inventory-select-trigger${
                        showProductDropdown ? ' inventory-select-trigger--open' : ''
                      }`}
                      onClick={() => setShowProductDropdown((prev) => !prev)}
                      disabled={groceriesLoading}
                    >
                      {selectedGroceryProduct
                        ? selectedGroceryProduct.productName
                        : groceriesLoading
                          ? 'Loading grocery products...'
                          : 'Select grocery product'}
                    </button>
                    {showProductDropdown && (
                      <div className="inventory-select-dropdown">
                        {groceryProducts.length >= SEARCHABLE_SELECT_MIN_OPTIONS && (
                          <div className="ingredients-search-wrap searchable-select-search">
                            <input
                              type="text"
                              className="ingredients-search-input"
                              value={productSearch}
                              onChange={(e) => setProductSearch(e.target.value)}
                              placeholder="Search by name"
                              autoFocus
                            />
                            {productSearch.trim() !== '' && (
                              <button
                                type="button"
                                className="ingredients-search-clear"
                                aria-label="Clear search"
                                onClick={() => setProductSearch('')}
                              >
                                <FontAwesomeIcon icon={faXmark} />
                              </button>
                            )}
                          </div>
                        )}
                        <div className="inventory-options-list">
                          {filteredGroceryProducts.length > 0 ? (
                            filteredGroceryProducts.map((product) => (
                              <button
                                key={product.id}
                                type="button"
                                className="inventory-option-item"
                                onClick={() => selectGroceryProduct(product)}
                              >
                                <span>{product.productName}</span>
                                {(product.productCode ||
                                  product.unitSymbol ||
                                  product.unitName) && (
                                  <span className="inventory-option-meta">
                                    {[product.productCode, product.unitSymbol || product.unitName]
                                      .filter(Boolean)
                                      .join(' · ')}
                                  </span>
                                )}
                              </button>
                            ))
                          ) : (
                            <div className="inventory-option-empty">No products found</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : null}

              {isStore ? (
                <>
                  {hasCategoryHierarchy ? (
                    <>
                      <div className="inventory-form-group">
                        <label htmlFor="inventory-parent-category-store">Category</label>
                        <select
                          id="inventory-parent-category-store"
                          name="parentCategoryId"
                          value={form.parentCategoryId}
                          onChange={handleChange}
                          required={!editingItemId}
                          disabled={categoriesLoading || (!!editingItemId && inventoryDetailLoading)}
                        >
                          <option value="">
                            {categoriesLoading ? 'Loading categories…' : 'Select category'}
                          </option>
                          {rootCategories.map((category) => (
                            <option key={getCategoryId(category)} value={getCategoryId(category)}>
                              {getCategoryName(category)}
                            </option>
                          ))}
                        </select>
                      </div>
                      {selectedParentHasChildren ? (
                        <div className="inventory-form-group">
                          <label htmlFor="inventory-grocery-category-store">Subcategory</label>
                          <select
                            id="inventory-grocery-category-store"
                            name="groceryCategoryId"
                            value={form.groceryCategoryId}
                            onChange={handleChange}
                            required={!editingItemId}
                            disabled={
                              categoriesLoading ||
                              !form.parentCategoryId ||
                              (!!editingItemId && inventoryDetailLoading)
                            }
                          >
                            <option value="">
                              {!form.parentCategoryId
                                ? 'Select category first'
                                : categoriesLoading
                                  ? 'Loading…'
                                  : 'Select subcategory'}
                            </option>
                            {subcategoryOptions.map((category) => (
                              <option key={getCategoryId(category)} value={getCategoryId(category)}>
                                {getCategoryName(category)}
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <div className="inventory-form-group">
                      <label htmlFor="inventory-grocery-category-store">Category</label>
                      <select
                        id="inventory-grocery-category-store"
                        name="groceryCategoryId"
                        value={form.groceryCategoryId}
                        onChange={handleChange}
                        required={!editingItemId}
                        disabled={categoriesLoading || (!!editingItemId && inventoryDetailLoading)}
                      >
                        <option value="">
                          {categoriesLoading ? 'Loading categories…' : 'Select category'}
                        </option>
                        {categoriesForForm.map((category) => (
                          <option key={getCategoryId(category)} value={getCategoryId(category)}>
                            {getCategoryName(category)}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="inventory-form-group">
                    <label htmlFor="inventory-brand-store">Brand</label>
                    <select
                      id="inventory-brand-store"
                      name="brandId"
                      value={form.brandId}
                      onChange={handleChange}
                      disabled={brandsLoading || (!!editingItemId && inventoryDetailLoading)}
                    >
                      <option value="">
                        {brandsLoading ? 'Loading brands…' : 'Select brand (optional)'}
                      </option>
                      {brands.map((brand) => (
                        <option
                          key={brand.brandId ?? brand.Brand_Id}
                          value={brand.brandId ?? brand.Brand_Id}
                        >
                          {brand.brandName ?? brand.Brand_Name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div
                    className="inventory-form-group inventory-form-span-full"
                    ref={productDropdownRef}
                  >
                    <label htmlFor="inventory-item-name">Item name</label>
                    <input
                      id="inventory-item-name"
                      type="text"
                      name="itemName"
                      value={form.itemName}
                      onChange={handleItemNameChange}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder={
                        groceriesLoading
                          ? 'Loading suggestions…'
                          : !storeCategorySelected
                            ? 'Select category first, then type or pick an item'
                            : 'Type to filter items in this category…'
                      }
                      autoComplete="off"
                      required
                      disabled={
                        (!!editingItemId && inventoryDetailLoading) ||
                        (!editingItemId && !storeCategorySelected)
                      }
                    />
                    {showProductDropdown &&
                    storeCategorySelected &&
                    itemNameSuggestions.length > 0 ? (
                      <div className="inventory-select-dropdown inventory-item-name-suggestions">
                        <div className="inventory-options-list">
                          {itemNameSuggestions.map((product) => (
                            <button
                              key={product.id}
                              type="button"
                              className="inventory-option-item"
                              onClick={() => selectGroceryProduct(product)}
                            >
                              <span>{product.productName}</span>
                              {(product.productCode ||
                                product.unitSymbol ||
                                product.unitName ||
                                product.groceryCategoryName) && (
                                <span className="inventory-option-meta">
                                  {[
                                    product.groceryCategoryName,
                                    product.unitSymbol || product.unitName,
                                    product.productCode,
                                  ]
                                    .filter(Boolean)
                                    .join(' · ')}
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                    {showProductDropdown &&
                    storeCategorySelected &&
                    !groceriesLoading &&
                    itemNameSuggestions.length === 0 ? (
                      <div className="inventory-select-dropdown inventory-item-name-suggestions">
                        <div className="inventory-option-empty">
                          No master items in this category
                          {String(form.itemName || '').trim()
                            ? ' matching your text'
                            : ''}
                          . You can still type a custom name.
                        </div>
                      </div>
                    ) : null}
                  </div>
                </>
              ) : null}

              {!isStore ? (
                <div className="inventory-form-group">
                  <label htmlFor="inventory-item-name">Item name</label>
                  <input
                    id="inventory-item-name"
                    type="text"
                    name="itemName"
                    value={form.itemName}
                    onChange={handleChange}
                    placeholder="Enter item name"
                    required
                    disabled={!!editingItemId && inventoryDetailLoading}
                  />
                </div>
              ) : null}

              {isStore ? (
                <>
                  <div className="inventory-form-group">
                    <label htmlFor="inventory-pack-qty-store">Pack quantity</label>
                    <div className="inventory-input-with-addon">
                      <input
                        id="inventory-pack-qty-store"
                        type="number"
                        name="packQuantity"
                        className="add-menu-form-field inventory-input--has-suffix"
                        value={form.packQuantity}
                        onChange={handleChange}
                        placeholder="e.g. 1 or 0.5"
                        min="0.01"
                        step="any"
                        required
                        disabled={!!editingItemId && inventoryDetailLoading}
                      />
                      <span
                        className="inventory-input-addon inventory-input-addon--suffix"
                        aria-hidden="true"
                      >
                        {(units.find((u) => String(getUnitId(u)) === String(form.unitId))
                          ? units.find((u) => String(getUnitId(u)) === String(form.unitId))
                              ?.symbol ||
                            getUnitName(
                              units.find((u) => String(getUnitId(u)) === String(form.unitId))
                            )
                          : '') ||
                          selectedGroceryProduct?.unitSymbol ||
                          selectedGroceryProduct?.unitName ||
                          'unit'}
                      </span>
                    </div>
                    <small className="inventory-field-hint">
                      Same product can be added again with a different pack size (e.g. 0.5 L and 1 L).
                    </small>
                  </div>
                  <div className="inventory-form-group">
                    <label htmlFor="inventory-unit-store">Unit</label>
                    <select
                      id="inventory-unit-store"
                      name="unitId"
                      value={form.unitId}
                      onChange={handleChange}
                      required
                      disabled={unitsLoading || (!!editingItemId && inventoryDetailLoading)}
                    >
                      <option value="">
                        {unitsLoading ? 'Loading units…' : 'Select unit (kg, g, L…)'}
                      </option>
                      {unitsForForm.map((unit) => (
                        <option key={getUnitId(unit)} value={getUnitId(unit)}>
                          {getUnitName(unit)}
                          {unit.symbol || unit.Symbol
                            ? ` (${unit.symbol || unit.Symbol})`
                            : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editingItemId ? (
                    <div className="inventory-form-group">
                      <label htmlFor="inventory-barcode" className="inventory-barcode-label">
                        <span>Barcode</span>
                        {!barcodeEditable ? (
                          <button
                            type="button"
                            className="inventory-barcode-change-btn"
                            onClick={() => setBarcodeEditable(true)}
                            disabled={inventoryDetailLoading}
                          >
                            Change
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="inventory-barcode-change-btn"
                            onClick={() => {
                              setBarcodeEditable(false);
                              setForm((prev) => ({ ...prev, barcode: savedBarcode }));
                            }}
                            disabled={inventoryDetailLoading}
                          >
                            Cancel
                          </button>
                        )}
                      </label>
                      <input
                        id="inventory-barcode"
                        type="text"
                        name="barcode"
                        value={form.barcode}
                        onChange={handleChange}
                        maxLength={100}
                        readOnly={!barcodeEditable}
                        disabled={inventoryDetailLoading || !barcodeEditable}
                        placeholder={barcodeEditable ? 'Enter barcode or leave blank to regenerate' : '—'}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}

              {!isStore && (
              <>
              <div className="inventory-form-group">
                <label htmlFor="inventory-grocery-category">Grocery category</label>
                <div className="inventory-searchable-select" ref={categoryDropdownRef}>
                  <button
                    type="button"
                    id="inventory-grocery-category"
                    className={`inventory-select-trigger${
                      showCategoryDropdown ? ' inventory-select-trigger--open' : ''
                    }`}
                    onClick={() => setShowCategoryDropdown((prev) => !prev)}
                    disabled={
                      (groceryProducts.length === 0 && categoriesLoading) ||
                      (!!editingItemId && inventoryDetailLoading)
                    }
                  >
                    {getCategoryName(selectedCategory) ||
                      (groceryProducts.length === 0 && categoriesLoading
                        ? 'Loading categories...'
                        : 'Select grocery category')}
                  </button>
                  {showCategoryDropdown && (
                    <div className="inventory-select-dropdown">
                      {categoriesForForm.length >= SEARCHABLE_SELECT_MIN_OPTIONS && (
                        <div className="ingredients-search-wrap searchable-select-search">
                          <input
                            type="text"
                            className="ingredients-search-input"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            placeholder="Search grocery categories"
                            autoFocus
                          />
                          {categorySearch.trim() !== '' && (
                            <button
                              type="button"
                              className="ingredients-search-clear"
                              aria-label="Clear search"
                              onClick={() => setCategorySearch('')}
                            >
                              <FontAwesomeIcon icon={faXmark} />
                            </button>
                          )}
                        </div>
                      )}
                      <div className="inventory-options-list">
                        {filteredCategories.length > 0 ? (
                          filteredCategories.map((category) => (
                            <button
                              key={getCategoryId(category)}
                              type="button"
                              className="inventory-option-item"
                              onClick={() => {
                                setForm((prev) => ({
                                  ...prev,
                                  groceryCategoryId: String(getCategoryId(category)),
                                }));
                                setShowCategoryDropdown(false);
                                setCategorySearch('');
                              }}
                            >
                              {getCategoryName(category)}
                            </button>
                          ))
                        ) : (
                          <div className="inventory-option-empty">No categories found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="inventory-form-group">
                <label htmlFor="inventory-unit">Unit</label>
                <div className="inventory-searchable-select" ref={unitDropdownRef}>
                  <button
                    type="button"
                    id="inventory-unit"
                    className={`inventory-select-trigger${
                      showUnitDropdown ? ' inventory-select-trigger--open' : ''
                    }`}
                    onClick={() => setShowUnitDropdown((prev) => !prev)}
                    disabled={
                      (groceryProducts.length === 0 && unitsLoading) ||
                      (!!editingItemId && inventoryDetailLoading)
                    }
                  >
                    {getUnitName(selectedUnit) ||
                      (groceryProducts.length === 0 && unitsLoading
                        ? 'Loading units...'
                        : 'Select unit')}
                  </button>
                  {showUnitDropdown && (
                    <div className="inventory-select-dropdown">
                      <div className="ingredients-search-wrap searchable-select-search">
                        <input
                          type="text"
                          className="ingredients-search-input"
                          value={unitSearch}
                          onChange={(e) => setUnitSearch(e.target.value)}
                          placeholder="Search units"
                          autoFocus
                        />
                        {unitSearch.trim() !== '' && (
                          <button
                            type="button"
                            className="ingredients-search-clear"
                            aria-label="Clear search"
                            onClick={() => setUnitSearch('')}
                          >
                            <FontAwesomeIcon icon={faXmark} />
                          </button>
                        )}
                      </div>
                      <div className="inventory-options-list">
                        {filteredUnits.length > 0 ? (
                          filteredUnits.map((unit) => (
                            <button
                              key={getUnitId(unit)}
                              type="button"
                              className="inventory-option-item"
                              onClick={() => {
                                setForm((prev) => ({ ...prev, unitId: String(getUnitId(unit)) }));
                                setShowUnitDropdown(false);
                                setUnitSearch('');
                              }}
                            >
                              {getUnitName(unit)}
                            </button>
                          ))
                        ) : (
                          <div className="inventory-option-empty">No units found</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              </>
              )}

              {isStore ? (
                <>
                  <div className="inventory-form-group">
                    <label htmlFor="inventory-mrp">MRP</label>
                    <input
                      id="inventory-mrp"
                      type="number"
                      name="mrp"
                      value={form.mrp}
                      onChange={handleChange}
                      placeholder="Enter MRP"
                      min="0"
                      step="0.01"
                      disabled={!!editingItemId && inventoryDetailLoading}
                    />
                  </div>
                  <div className="inventory-form-group">
                    <label htmlFor="inventory-unit-price">Selling price</label>
                    <input
                      id="inventory-unit-price"
                      type="number"
                      name="unitPrice"
                      value={form.unitPrice}
                      onChange={handleChange}
                      placeholder="Enter selling price"
                      min="0"
                      step="0.01"
                      required
                      disabled={!!editingItemId && inventoryDetailLoading}
                    />
                  </div>
                </>
              ) : (
                <div className="inventory-form-group">
                  <label htmlFor="inventory-unit-price">Unit price</label>
                  <input
                    id="inventory-unit-price"
                    type="number"
                    name="unitPrice"
                    value={form.unitPrice}
                    onChange={handleChange}
                    placeholder="Enter unit price"
                    min="0"
                    step="0.01"
                    required
                    disabled={!!editingItemId && inventoryDetailLoading}
                  />
                </div>
              )}

              <div className="inventory-form-group inventory-form-span-full">
                <label htmlFor="inventory-description">Description</label>
                <input
                  id="inventory-description"
                  type="text"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  placeholder="Enter description"
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
              </div>

              <div className="inventory-form-group">
                <label htmlFor="inventory-mfg-date">Mfg date</label>
                <input
                  id="inventory-mfg-date"
                  type="date"
                  name="mfgDate"
                  className="add-menu-form-field"
                  value={form.mfgDate}
                  onChange={handleChange}
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label htmlFor="inventory-exp-date">Exp date</label>
                <input
                  id="inventory-exp-date"
                  type="date"
                  name="expDate"
                  className="add-menu-form-field"
                  value={form.expDate}
                  onChange={handleChange}
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
              </div>
              <div className="inventory-form-group inventory-form-span-full">
                <label htmlFor="inventory-image">Image</label>
                <input
                  id="inventory-image"
                  type="file"
                  name="imageFile"
                  accept="image/*"
                  onChange={handleChange}
                  className="inventory-file-input"
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
                {form.existingImageUrl && !form.imageFile ? (
                  <img
                    src={form.existingImageUrl}
                    alt=""
                    className="inventory-form-image-preview"
                  />
                ) : null}
              </div>

              {isStore ? (
                <div className="inventory-form-group">
                  <label htmlFor="inventory-status">Status</label>
                  <select
                    id="inventory-status"
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={!!editingItemId && inventoryDetailLoading}
                  >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </div>
              ) : null}

              <div className="inventory-form-section-label inventory-form-span-full">
                {editingItemId ? 'Inventory level' : 'Initial stock & reorder'}
              </div>
              <div className="inventory-form-group">
                <label htmlFor="inventory-qty">Quantity in stock</label>
                <input
                  id="inventory-qty"
                  type="number"
                  name="quantityInStock"
                  value={form.quantityInStock}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  required
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label htmlFor="inventory-reorder-level">Reorder level</label>
                <input
                  id="inventory-reorder-level"
                  type="number"
                  name="reorderLevel"
                  value={form.reorderLevel}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  required
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label htmlFor="inventory-reorder-qty">Reorder quantity</label>
                <input
                  id="inventory-reorder-qty"
                  type="number"
                  name="reorderQuantity"
                  value={form.reorderQuantity}
                  onChange={handleChange}
                  min="0"
                  step="any"
                  required
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
              </div>
              <div className="inventory-form-group">
                <label htmlFor="inventory-comments">Comments</label>
                <input
                  id="inventory-comments"
                  type="text"
                  name="comments"
                  value={form.comments}
                  onChange={handleChange}
                  placeholder={editingItemId ? 'Notes on stock or reordering' : 'e.g. Initial stock'}
                  disabled={!!editingItemId && inventoryDetailLoading}
                />
              </div>
              </div>

              <div className="inventory-item-form-footer">
                <button
                  type="submit"
                  className="add-button"
                  disabled={isSubmitting || inventoryDetailLoading}
                >
                  {isSubmitting ? 'Saving...' : editingItemId ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inventory;
